import {createAsyncThunk} from '@reduxjs/toolkit';
import {getJWTToken} from '../../lib/auth-token';
import {handleAuthFailure, signOutExpiredSession} from '../../lib/auth-error-handler';
import {getWorkerApiUrl, apiFetch} from '../../lib/api';
import {debugLog} from '../../lib/debug';
import {ApiError, isApiError, toApiError, toNetworkError} from '../../lib/apiError';
import {R2UploadError} from '../../lib/r2Upload';
import {runResumableUpload, ResumableUploadApi} from '../../lib/resumableUpload';
import {forgetFileHandle} from '../../lib/fileHandles';
import {AudioUpload} from '../../domain/types';
import type {
  AudioUpload as WireAudioUpload,
  AudioUploadPartsResponse,
  CreateAudioUploadUrlRequest,
  CreateAudioUploadUrlResponse,
  CompleteAudioUploadResponse,
  DeleteAudioUploadResponse,
  ListAudioUploadsResponse,
} from '../../backend-types/transcribe-api';
import {setUploadPhase, setUploadProgress, setUploadingAudioId} from './audioSlice';

/** Wire (snake_case) → domain (camelCase). */
function toDomain(wire: WireAudioUpload): AudioUpload {
  return {
    id: wire.id,
    projectId: wire.project_id,
    name: wire.name,
    r2Key: wire.r2_key,
    sizeBytes: wire.size,
    clientSizeBytes: wire.client_size ?? null,
    clientLastModified: wire.client_last_modified ?? null,
    contentType: wire.content_type,
    duration: wire.duration_seconds ?? undefined,
    durationTicks: wire.duration_ticks ?? undefined,
    sampleRate: wire.sample_rate ?? undefined,
    localAudioPath: wire.local_audio_path,
    status: wire.status,
    r2UploadId: wire.r2_upload_id ?? null,
    partSize: wire.part_size ?? null,
    createdAt: wire.created_at,
    updatedAt: wire.updated_at,
  };
}

async function authorizedJson(
  path: string,
  init: {method: string; body?: string},
): Promise<{ok: boolean; status: number; text: string}> {
  const jwt = await getJWTToken();

  if (!jwt) {
    throw new Error('No authentication token available');
  }

  const headers: Record<string, string> = {Authorization: `Bearer ${jwt}`};
  if (init.body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }

  const response = await apiFetch(`${getWorkerApiUrl()}${path}`, {
    method: init.method,
    headers,
    body: init.body,
  });

  return {ok: response.ok, status: response.status, text: await response.text()};
}

/**
 * Shared machinery for both halves of an upload: fetching the next batch of parts, and confirming.
 *
 * `/parts` is passed the size and lastModified of the file in hand every time — not just on the
 * first call — because it is the server's only way to tell that the file being fed into an upload
 * is still the one it was started with. R2 will otherwise stitch parts from two different
 * recordings into one object and complete it without complaining.
 */
function uploadApiFor(
  audioId: string,
  file: File,
  dispatch: (action: unknown) => void,
): ResumableUploadApi {
  const partsQuery = `size=${file.size}&lastModified=${file.lastModified}`;

  return {
    fetchParts: async () => {
      const response = await authorizedJson(
        `/rest/v1/audio/${audioId}/parts?${partsQuery}`,
        {method: 'GET'},
      );

      if (!response.ok) {
        if (response.status === 401) {
          signOutExpiredSession(dispatch);
        }
        throw toApiError(response.status, response.text, 'Could not work out what still needs uploading');
      }

      const data: AudioUploadPartsResponse = JSON.parse(response.text);
      debugLog(
        '[Audio] Parts for', audioId,
        '— have', data.upload.uploadedParts, 'of', data.upload.partCount,
        ', issued', data.upload.parts.length,
      );
      return data;
    },

    complete: async () => {
      const response = await authorizedJson(`/rest/v1/audio/${audioId}/complete`, {method: 'POST'});

      if (!response.ok) {
        if (response.status === 401) {
          signOutExpiredSession(dispatch);
        }
        // A 400 here carries `missingParts`; `runResumableUpload` reads that off the ApiError and
        // goes back for another round rather than treating it as the end.
        throw toApiError(response.status, response.text, 'The upload finished but could not be confirmed');
      }

      const {audio}: CompleteAudioUploadResponse = JSON.parse(response.text);
      debugLog('[Audio] Upload confirmed:', audio.id, audio.status);
      return audio;
    },
  };
}

/** Turns whatever came out of the upload machinery into the shape `rejectWithValue` wants. */
function toUploadRejection(error: unknown, fallback: string, audioId: string | null): ApiError {
  const base = (() => {
    if (error instanceof R2UploadError && error.aborted) {
      return {status: 0, message: 'Upload cancelled'};
    }
    if (isApiError(error)) {
      return error;
    }
    console.error('[Audio] Upload failed:', error);
    return toNetworkError(error, fallback);
  })();

  // The row survives a failed upload and is still resumable, so the caller is told which one it is.
  return audioId ? {...base, audioId} : base;
}

/**
 * Step 1 of two: get the audio into R2, in resumable parts.
 *
 * Reserve → PUT every part → confirm is one thunk because it is one user action, but it is no
 * longer all-or-nothing: the file is cut into fixed-size parts that go up independently, and R2
 * remembers which ones landed. A drop now costs one part rather than the whole transfer, and what
 * it leaves behind is a `pending` row that `resumeAudioUpload` can carry on — the orphan rows this
 * replaced could only be deleted. Transcribing is deliberately *not* part of this — see
 * `startTranscription`.
 *
 * `size` and `lastModified` are sent up front and are not optional: the part count is derived from
 * the size, and the pair is the same-file guard every later `/parts` call is checked against.
 */
export const uploadAudioFile = createAsyncThunk<
  AudioUpload,
  {
    file: File;
    projectId?: string;
    localAudioPath?: string;
    // Required, not optional: the backend 400s on a missing/zero/NaN duration, and a param that can
    // silently be omitted invites a fallback that would send one of those instead of refusing
    // client-side. mediaInfo.js is the only source for this — see `useAudioUploadQueue`, the one
    // caller, which never reaches this thunk if the probe fails.
    durationSeconds: number;
    durationTicks?: number;
    sampleRate?: number;
    signal?: AbortSignal;
  },
  {rejectValue: ApiError}
  >(
    'audio/upload',
    async ({
      file,
      projectId,
      localAudioPath,
      durationSeconds,
      durationTicks,
      sampleRate,
      signal
    }, {rejectWithValue, dispatch}) => {
      // Hoisted out of the try: a failure after the row exists still has to name it, because that
      // row is a resumable upload rather than something to clean up.
      let createdAudioId: string | null = null;

      try {
        dispatch(setUploadPhase('requesting'));
        const requestBody: CreateAudioUploadUrlRequest = {
          filename: file.name,
          durationSeconds,
          size: file.size,
          lastModified: file.lastModified,
          ...(file.type ? {contentType: file.type} : {}),
          ...(projectId ? {projectId} : {}),
          ...(localAudioPath ? {localAudioPath} : {}),
          ...(durationTicks ? {durationTicks} : {}),
          ...(sampleRate ? {sampleRate} : {}),
        };

        const created = await authorizedJson('/rest/v1/audio/upload-url', {
          method: 'POST',
          body: JSON.stringify(requestBody),
        });

        if (!created.ok) {
          if (created.status === 401) {
            signOutExpiredSession(dispatch);
          }
          return rejectWithValue(toApiError(created.status, created.text, 'Could not start the upload'));
        }

        const {audio, upload}: CreateAudioUploadUrlResponse = JSON.parse(created.text);
        debugLog(
          '[Audio] Upload started for', audio.id,
          '—', upload.partCount, 'parts of', upload.partSize, 'bytes, expires', upload.expiresAt,
        );

        // Published before the first byte moves so a reload mid-upload has a row to resume from,
        // and so the row can show itself as the one in flight.
        createdAudioId = audio.id;
        dispatch(setUploadingAudioId(audio.id));
        dispatch(setUploadPhase('uploading'));

        const readyAudio = await runResumableUpload({
          file,
          partSize: upload.partSize,
          partCount: upload.partCount,
          parts: upload.parts,
          uploadedParts: 0,
          uploadedBytes: 0,
          api: uploadApiFor(audio.id, file, dispatch),
          onProgress: progress => dispatch(setUploadProgress(progress)),
          onFinalizing: () => dispatch(setUploadPhase('finalizing')),
          signal,
        });

        // mediaInfo.js is the only place these are known accurately client-side; the record only
        // carries what we sent, so keep ours if the server has nothing.
        return {
          ...toDomain(readyAudio),
          duration: readyAudio.duration_seconds ?? durationSeconds,
          durationTicks: readyAudio.duration_ticks ?? durationTicks,
          sampleRate: readyAudio.sample_rate ?? sampleRate,
        };
      } catch (error) {
        handleAuthFailure(error, dispatch);
        return rejectWithValue(toUploadRejection(error, 'The upload failed', createdAudioId));
      }
    }
  );

/**
 * Carries a `pending` upload the rest of the way, given the same file again.
 *
 * Nothing about what has already landed is cached in the browser — the first thing this does is ask
 * R2, through `/parts`, which is why a resume survives a reload, a crash, a different browser or a
 * different machine. All the caller has to supply is the file itself.
 *
 * The two refusals it can come back with are both meaningful to the user and are passed through as
 * they are, for the workspace to turn into the right offer: `fileMismatch` on a 409 means the wrong
 * file was picked, and `resumable: false` on a 409 or 410 means this upload is genuinely finished
 * as a thing that can be continued.
 */
export const resumeAudioUpload = createAsyncThunk<
  AudioUpload,
  {audio: AudioUpload; file: File; signal?: AbortSignal},
  {rejectValue: ApiError}
>(
  'audio/resumeUpload',
  async ({audio, file, signal}, {rejectWithValue, dispatch}) => {
    try {
      const api = uploadApiFor(audio.id, file, dispatch);

      dispatch(setUploadingAudioId(audio.id));
      dispatch(setUploadPhase('resuming'));

      let upload;
      try {
        ({upload} = await api.fetchParts());
      } catch (error) {
        // `/parts` answers 409 for an upload that is already complete as well as for one that never
        // could be resumed, and the two are told apart by asking. `/complete` is idempotent — it
        // confirms from the object itself — so if the bytes really are all there this turns a race
        // (another tab, or a `/complete` whose response was lost) into the success it actually is,
        // and otherwise the original refusal stands.
        if (isApiError(error) && error.status === 409 && !error.fileMismatch) {
          dispatch(setUploadPhase('finalizing'));
          const alreadyDone = await api.complete().catch(() => null);

          if (alreadyDone) {
            void forgetFileHandle(audio.id);
            return {
              ...toDomain(alreadyDone),
              duration: alreadyDone.duration_seconds ?? audio.duration,
              durationTicks: alreadyDone.duration_ticks ?? audio.durationTicks,
              sampleRate: alreadyDone.sample_rate ?? audio.sampleRate,
            };
          }
        }
        throw error;
      }

      dispatch(setUploadPhase('uploading'));

      const readyAudio = await runResumableUpload({
        file,
        partSize: upload.partSize,
        partCount: upload.partCount,
        parts: upload.parts,
        uploadedParts: upload.uploadedParts,
        uploadedBytes: upload.uploadedBytes,
        api,
        onProgress: progress => dispatch(setUploadProgress(progress)),
        onFinalizing: () => dispatch(setUploadPhase('finalizing')),
        signal,
      });

      // The file is in R2; there is nothing left to re-open it for.
      void forgetFileHandle(audio.id);

      // The upload record only carries what was sent when it was created, so the row's own probed
      // values are still the better ones where the server has none.
      return {
        ...toDomain(readyAudio),
        duration: readyAudio.duration_seconds ?? audio.duration,
        durationTicks: readyAudio.duration_ticks ?? audio.durationTicks,
        sampleRate: readyAudio.sample_rate ?? audio.sampleRate,
      };
    } catch (error) {
      handleAuthFailure(error, dispatch);
      return rejectWithValue(toUploadRejection(error, 'The upload could not be resumed', audio.id));
    }
  }
);

/** The workspace's file list — uploaded audio, transcribed or not. `'all'` means every project. */
export const fetchAudioUploads = createAsyncThunk<
  AudioUpload[],
  string,
  {rejectValue: ApiError}
>(
  'audio/fetchAll',
  async (projectFilter, {rejectWithValue, dispatch}) => {
    try {
      const query = projectFilter && projectFilter !== 'all'
        ? `?projectId=${encodeURIComponent(projectFilter)}`
        : '';

      const response = await authorizedJson(`/rest/v1/audio${query}`, {method: 'GET'});

      if (!response.ok) {
        if (response.status === 401) {
          signOutExpiredSession(dispatch);
        }
        return rejectWithValue(toApiError(response.status, response.text, 'Could not load your audio files'));
      }

      const data: ListAudioUploadsResponse = JSON.parse(response.text);
      debugLog('[Audio] Fetched', data.audio.length, 'audio uploads');

      return data.audio.map(toDomain);
    } catch (error) {
      handleAuthFailure(error, dispatch);
      return rejectWithValue(toNetworkError(error, 'Could not load your audio files'));
    }
  }
);

/** Deletes the row and the R2 object. Transcriptions already made from it are unaffected. */
export const deleteAudioUpload = createAsyncThunk<
  string,
  string,
  {rejectValue: ApiError}
>(
  'audio/delete',
  async (audioId, {rejectWithValue, dispatch}) => {
    try {
      const response = await authorizedJson(`/rest/v1/audio/${audioId}`, {method: 'DELETE'});

      if (!response.ok) {
        if (response.status === 401) {
          signOutExpiredSession(dispatch);
        }
        return rejectWithValue(toApiError(response.status, response.text, 'Could not delete the audio file'));
      }

      const data: DeleteAudioUploadResponse = JSON.parse(response.text);
      debugLog('[Audio] Deleted audio upload:', data.id);

      // Nothing left to resume, so the remembered file handle is dead weight.
      void forgetFileHandle(data.id);

      return data.id;
    } catch (error) {
      handleAuthFailure(error, dispatch);
      return rejectWithValue(toNetworkError(error, 'Could not delete the audio file'));
    }
  }
);
